import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useGameBase from '../../hooks/useGameBase';
import SpectatorPerspectiveSelector from './SpectatorPerspectiveSelector';

const EMPTY_GRID = Array.from({ length: 4 }, () => Array(4).fill(0));
const SWIPE_THRESHOLD = 24;
const MAX_DRAG_OFFSET = 34;

function getTileClasses(value) {
  const palette = {
    0: 'bg-slate-200/80 text-transparent dark:bg-slate-800/70',
    2: 'bg-stone-100 text-stone-700 dark:bg-stone-200 dark:text-stone-900',
    4: 'bg-orange-100 text-orange-700 dark:bg-orange-200 dark:text-orange-950',
    8: 'bg-orange-300 text-white dark:bg-orange-400 dark:text-white',
    16: 'bg-orange-400 text-white dark:bg-orange-500 dark:text-white',
    32: 'bg-orange-500 text-white dark:bg-orange-600 dark:text-white',
    64: 'bg-red-500 text-white dark:bg-red-600 dark:text-white',
    128: 'bg-amber-400 text-white dark:bg-amber-500 dark:text-white',
    256: 'bg-amber-500 text-white dark:bg-amber-600 dark:text-white',
    512: 'bg-yellow-400 text-slate-900 dark:bg-yellow-500 dark:text-slate-950',
    1024: 'bg-yellow-300 text-slate-900 dark:bg-yellow-400 dark:text-slate-950',
    2048: 'bg-emerald-400 text-white dark:bg-emerald-500 dark:text-white',
  };

  return palette[value] || 'bg-fuchsia-400 text-white dark:bg-fuchsia-500 dark:text-white';
}

function tileTextSize(value, compact = false) {
  if (compact) {
    return value >= 1024 ? 'text-[10px]' : value >= 128 ? 'text-xs' : 'text-sm';
  }
  return value >= 1024 ? 'text-xl sm:text-2xl' : value >= 128 ? 'text-2xl sm:text-3xl' : 'text-3xl sm:text-4xl';
}

function Tile({ value, compact = false, animated = false }) {
  return (
    <div
      className={`flex aspect-square items-center justify-center rounded-2xl font-black tracking-tight shadow-sm transition-all duration-200 ease-out ${animated ? 'scale-[1.08] brightness-110 ring-2 ring-white/35 dark:ring-white/20' : 'scale-100'} ${getTileClasses(value)} ${tileTextSize(value, compact)}`}
    >
      {value > 0 ? value : ''}
    </div>
  );
}

function Board({
  grid,
  compact = false,
  highlight = false,
  animatedCells = null,
  dragOffset = null,
  onPointerDown,
  onPointerMove,
  onPointerUp,
  onPointerCancel,
}) {
  const wrapperClass = compact
    ? 'select-none rounded-2xl border border-slate-200 bg-slate-300/70 p-2 dark:border-slate-700 dark:bg-slate-900/80'
    : `select-none rounded-[28px] border p-3 sm:p-4 ${highlight ? 'border-cyan-300 bg-white/90 dark:border-cyan-700 dark:bg-slate-950/90' : 'border-slate-200 bg-white/90 dark:border-slate-800 dark:bg-slate-950/80'}`;
  const translateX = compact ? 0 : Math.max(-MAX_DRAG_OFFSET, Math.min(MAX_DRAG_OFFSET, Number(dragOffset?.x) || 0));
  const translateY = compact ? 0 : Math.max(-MAX_DRAG_OFFSET, Math.min(MAX_DRAG_OFFSET, Number(dragOffset?.y) || 0));
  const activeGesture = !compact && (!!translateX || !!translateY);
  const animatedCellSet = animatedCells instanceof Set ? animatedCells : null;

  return (
    <div
      className={wrapperClass}
      onPointerDown={compact ? undefined : onPointerDown}
      onPointerMove={compact ? undefined : onPointerMove}
      onPointerUp={compact ? undefined : onPointerUp}
      onPointerCancel={compact ? undefined : onPointerCancel}
      style={compact ? { userSelect: 'none', WebkitUserSelect: 'none' } : { touchAction: 'none', userSelect: 'none', WebkitUserSelect: 'none' }}
    >
      <div
        className={`grid grid-cols-4 ${compact ? 'gap-1.5' : 'gap-2 sm:gap-3'} ${compact ? '' : 'transition-transform duration-150 ease-out'}`}
        style={compact ? undefined : { transform: `translate(${translateX}px, ${translateY}px)`, opacity: activeGesture ? 0.97 : 1 }}
      >
        {(Array.isArray(grid) ? grid : EMPTY_GRID).map((row, rowIndex) => row.map((value, colIndex) => (
          <Tile
            key={`${rowIndex}-${colIndex}`}
            value={Number(value) || 0}
            compact={compact}
            animated={!!animatedCellSet?.has(`${rowIndex}-${colIndex}`)}
          />
        )))}
      </div>
    </div>
  );
}

function ControlButton({ direction, label, onPress, disabled }) {
  return (
    <button
      type="button"
      onClick={() => onPress(direction)}
      disabled={disabled}
      className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-bold text-slate-800 shadow-sm transition hover:border-cyan-300 hover:text-cyan-700 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:hover:border-cyan-700 dark:hover:text-cyan-300"
    >
      {label}
    </button>
  );
}

function Game2048({ roomId, gameState = {}, gamePlayers = [], user = {}, socket, isSpectator = false }) {
  const perspectiveOptions = useMemo(() => {
    if (Array.isArray(gamePlayers) && gamePlayers.length > 0) return gamePlayers;
    if (Array.isArray(gameState?.players) && gameState.players.length > 0) return gameState.players;
    return [];
  }, [gamePlayers, gameState?.players]);

  const [spectatorViewPlayerId, setSpectatorViewPlayerId] = useState(null);
  const [expandedPlayerId, setExpandedPlayerId] = useState(null);
  const [animatedCells, setAnimatedCells] = useState(() => new Set());
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const pointerStateRef = useRef(null);
  const previousGridRef = useRef(null);

  const perspectivePlayer = useMemo(() => {
    if (!isSpectator || !spectatorViewPlayerId) return null;
    return perspectiveOptions.find((player) => {
      const playerId = player?.userId || player?.socketId || player?.id || player?.username;
      return String(playerId) === String(spectatorViewPlayerId);
    }) || null;
  }, [isSpectator, perspectiveOptions, spectatorViewPlayerId]);

  useEffect(() => {
    if (!isSpectator) {
      setSpectatorViewPlayerId(null);
      return;
    }
    if (!perspectiveOptions.length) {
      setSpectatorViewPlayerId(null);
      return;
    }
    const exists = perspectiveOptions.some((player) => {
      const playerId = player?.userId || player?.socketId || player?.id || player?.username;
      return String(playerId) === String(spectatorViewPlayerId);
    });
    if (!exists) {
      const first = perspectiveOptions[0];
      setSpectatorViewPlayerId(first?.userId || first?.socketId || first?.id || first?.username || null);
    }
  }, [isSpectator, perspectiveOptions, spectatorViewPlayerId]);

  const { submitAction, perspectiveIds } = useGameBase({ gameState, gamePlayers, user, socket, roomId, perspectivePlayer });

  const playerProgress = useMemo(() => gameState?.playerProgress || {}, [gameState?.playerProgress]);
  const players = Array.isArray(gameState?.players) && gameState.players.length > 0 ? gameState.players : gamePlayers;
  const myId = useMemo(() => {
    for (const candidate of perspectiveIds || []) {
      if (playerProgress[candidate]) return candidate;
    }
    if (!isSpectator) {
      return socket?.id || user?.socketId || user?.userId || null;
    }
    return null;
  }, [isSpectator, perspectiveIds, playerProgress, socket?.id, user?.socketId, user?.userId]);

  const myProgress = myId ? playerProgress[myId] || null : null;
  const myGrid = myProgress?.grid || EMPTY_GRID;
  const finished = !!gameState?.finished;
  const winners = useMemo(() => (Array.isArray(gameState?.winners) ? gameState.winners : []), [gameState?.winners]);
  const canInteract = !isSpectator && !!myProgress && !finished && myProgress.status === 'active';

  const winnerNames = useMemo(() => winners.map((winnerId) => {
    const player = players.find((entry) => (entry.socketId || entry.userId || entry.id) === winnerId);
    return player?.username || 'Player';
  }), [players, winners]);

  const progressRows = useMemo(() => players.map((player) => {
    const playerId = player.socketId || player.userId || player.id;
    const progress = playerProgress[playerId] || null;
    const isViewed = perspectiveIds.includes(String(playerId));
    return {
      id: playerId,
      username: player.username || 'Player',
      grid: progress?.grid || EMPTY_GRID,
      boardScore: Number(progress?.score) || 0,
      maxTile: Number(progress?.maxTile) || 0,
      moveCount: Number(progress?.moveCount) || 0,
      status: progress?.status || 'active',
      achievedTarget: !!progress?.achievedTarget,
      isViewed,
      isWinner: winners.includes(playerId),
      accent: player.color || null,
    };
  }), [playerProgress, players, perspectiveIds, winners]);

  const otherPlayers = useMemo(
    () => progressRows.filter((entry) => entry.id !== myId),
    [myId, progressRows]
  );

  const submitMove = useCallback((direction) => {
    if (!canInteract) return;
    submitAction({ type: 'move', direction }).catch(() => {});
  }, [canInteract, submitAction]);

  useEffect(() => {
    const previousGrid = previousGridRef.current;
    if (!myGrid || !Array.isArray(myGrid)) {
      previousGridRef.current = myGrid;
      return undefined;
    }

    if (!previousGrid) {
      previousGridRef.current = myGrid.map((row) => row.slice());
      return undefined;
    }

    const changed = new Set();
    for (let row = 0; row < 4; row += 1) {
      for (let col = 0; col < 4; col += 1) {
        if ((previousGrid[row]?.[col] || 0) !== (myGrid[row]?.[col] || 0)) {
          changed.add(`${row}-${col}`);
        }
      }
    }

    previousGridRef.current = myGrid.map((row) => row.slice());
    if (!changed.size) {
      return undefined;
    }

    setAnimatedCells(changed);
    const timeoutId = window.setTimeout(() => {
      setAnimatedCells(new Set());
    }, 190);

    return () => window.clearTimeout(timeoutId);
  }, [myGrid]);

  useEffect(() => {
    if (isSpectator) return undefined;

    const directionForKey = {
      ArrowUp: 'up',
      ArrowDown: 'down',
      ArrowLeft: 'left',
      ArrowRight: 'right',
      w: 'up',
      a: 'left',
      s: 'down',
      d: 'right',
    };

    const handleKeyDown = (event) => {
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      const direction = directionForKey[event.key];
      if (!direction) return;
      event.preventDefault();
      submitMove(direction);
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isSpectator, submitMove]);

  const resolveGestureDirection = useCallback((deltaX, deltaY) => {
    if (Math.max(Math.abs(deltaX), Math.abs(deltaY)) < SWIPE_THRESHOLD) return null;
    if (Math.abs(deltaX) > Math.abs(deltaY)) {
      return deltaX > 0 ? 'right' : 'left';
    }
    return deltaY > 0 ? 'down' : 'up';
  }, []);

  const clearGesture = useCallback(() => {
    pointerStateRef.current = null;
    setDragOffset({ x: 0, y: 0 });
  }, []);

  const handleBoardPointerDown = useCallback((event) => {
    if (!canInteract || event.button === 2) return;
    pointerStateRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
    };
    setDragOffset({ x: 0, y: 0 });
    if (typeof event.currentTarget?.setPointerCapture === 'function') {
      try {
        event.currentTarget.setPointerCapture(event.pointerId);
      } catch (error) {
      }
    }
  }, [canInteract]);

  const handleBoardPointerMove = useCallback((event) => {
    const gesture = pointerStateRef.current;
    if (!gesture || gesture.pointerId !== event.pointerId) return;
    setDragOffset({
      x: event.clientX - gesture.startX,
      y: event.clientY - gesture.startY,
    });
  }, []);

  const finalizeGesture = useCallback((event) => {
    const gesture = pointerStateRef.current;
    if (!gesture || gesture.pointerId !== event.pointerId) return;

    const deltaX = event.clientX - gesture.startX;
    const deltaY = event.clientY - gesture.startY;
    const direction = resolveGestureDirection(deltaX, deltaY);

    if (typeof event.currentTarget?.releasePointerCapture === 'function') {
      try {
        event.currentTarget.releasePointerCapture(event.pointerId);
      } catch (error) {
      }
    }

    clearGesture();
    if (direction) {
      submitMove(direction);
    }
  }, [clearGesture, resolveGestureDirection, submitMove]);

  const handleBoardPointerCancel = useCallback((event) => {
    const gesture = pointerStateRef.current;
    if (!gesture || gesture.pointerId !== event.pointerId) return;
    if (typeof event.currentTarget?.releasePointerCapture === 'function') {
      try {
        event.currentTarget.releasePointerCapture(event.pointerId);
      } catch (error) {
      }
    }
    clearGesture();
  }, [clearGesture]);

  const finishedText = useMemo(() => {
    if (!finished) return null;
    if (!winnerNames.length) return 'Round over.';
    if (winnerNames.length === 1) return `${winnerNames[0]} wins.`;
    return `Draw between ${winnerNames.join(', ')}.`;
  }, [finished, winnerNames]);

  return (
    <div className="mx-auto flex w-full max-w-6xl flex-col gap-4 p-3 text-slate-900 dark:text-slate-100 sm:p-4">
      {isSpectator && perspectiveOptions.length > 0 ? (
        <SpectatorPerspectiveSelector
          players={perspectiveOptions}
          value={spectatorViewPlayerId || ''}
          onChange={setSpectatorViewPlayerId}
          className="max-w-sm"
        />
      ) : null}

      <div className="overflow-hidden rounded-[32px] border border-amber-200/70 bg-gradient-to-br from-amber-50 via-white to-orange-50 shadow-[0_28px_80px_rgba(15,23,42,0.14)] dark:border-slate-800 dark:from-slate-950 dark:via-slate-900 dark:to-orange-950/40">
        <div className="p-4 sm:p-6">
          <div className="min-w-0">

            <div className="mb-4 grid grid-cols-3 gap-3">
              <div className="rounded-2xl border border-slate-200 bg-white/80 px-4 py-3 dark:border-slate-800 dark:bg-slate-950/70">
                <div className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 dark:text-slate-400">Score</div>
                <div className="mt-1 text-2xl font-black">{myProgress?.score || 0}</div>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-white/80 px-4 py-3 dark:border-slate-800 dark:bg-slate-950/70">
                <div className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 dark:text-slate-400">Best Tile</div>
                <div className="mt-1 text-2xl font-black">{myProgress?.maxTile || 0}</div>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-white/80 px-4 py-3 dark:border-slate-800 dark:bg-slate-950/70">
                <div className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 dark:text-slate-400">Moves</div>
                <div className="mt-1 text-2xl font-black">{myProgress?.moveCount || 0}</div>
              </div>
            </div>

            <Board
              grid={myGrid}
              highlight
              animatedCells={animatedCells}
              dragOffset={dragOffset}
              onPointerDown={handleBoardPointerDown}
              onPointerMove={handleBoardPointerMove}
              onPointerUp={finalizeGesture}
              onPointerCancel={handleBoardPointerCancel}
            />

            {finishedText ? (
              <div className="mt-4 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-semibold text-emerald-800 dark:border-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-200">
                {finishedText}
              </div>
            ) : null}

            {!isSpectator ? (
              <div className="mt-4 grid grid-cols-3 gap-2 sm:max-w-sm">
                <div />
                <ControlButton direction="up" label="Up" onPress={submitMove} disabled={!canInteract} />
                <div />
                <ControlButton direction="left" label="Left" onPress={submitMove} disabled={!canInteract} />
                <ControlButton direction="down" label="Down" onPress={submitMove} disabled={!canInteract} />
                <ControlButton direction="right" label="Right" onPress={submitMove} disabled={!canInteract} />
              </div>
            ) : null}

            {!isSpectator ? (
              <div className="mt-3 text-xs text-slate-500 dark:text-slate-400">
                Swipe the board on mobile, or use the arrow keys or the direction buttons.
              </div>
            ) : null}

            <div className="mt-5 rounded-[28px] border border-slate-200 bg-white/80 p-4 dark:border-slate-800 dark:bg-slate-950/70">
              <div className="mb-3 flex items-center justify-between gap-2">
                <h3 className="text-lg font-black tracking-tight">Other Players</h3>
                <div className="text-xs uppercase tracking-[0.2em] text-slate-500 dark:text-slate-400">Tap a score to expand</div>
              </div>

              <div className="space-y-3">
                {otherPlayers.length === 0 ? (
                  <div className="rounded-2xl border border-slate-200 bg-white/70 px-4 py-3 text-sm text-slate-500 dark:border-slate-800 dark:bg-slate-950/40 dark:text-slate-400">
                    No other players in this room yet.
                  </div>
                ) : otherPlayers.map((entry) => {
                  const expanded = expandedPlayerId === entry.id;
                  return (
                    <div
                      key={entry.id}
                      className={`rounded-2xl border p-3 ${entry.isViewed ? 'border-cyan-300 bg-cyan-50 dark:border-cyan-700 dark:bg-cyan-950/30' : 'border-slate-200 bg-white/70 dark:border-slate-800 dark:bg-slate-950/40'}`}
                      
                    >
                      <div className="flex flex-wrap items-center justify-between gap-3">
                        <div>
                          <div className="font-semibold">{entry.username}</div>
                          <div className={`mt-1 text-xs font-semibold uppercase tracking-[0.18em] ${entry.isWinner || entry.achievedTarget ? 'text-emerald-600 dark:text-emerald-300' : entry.status === 'out' ? 'text-rose-600 dark:text-rose-300' : 'text-slate-500 dark:text-slate-400'}`}>
                            {entry.isWinner || entry.achievedTarget ? 'Won' : entry.status === 'out' ? 'No moves' : 'Playing'}
                          </div>
                        </div>

                        <div className="flex flex-wrap items-center gap-2 text-sm text-slate-600 dark:text-slate-300">
                          <button
                            type="button"
                            onClick={() => setExpandedPlayerId(expanded ? null : entry.id)}
                            className="rounded-xl border border-slate-200 bg-white px-3 py-2 font-semibold text-slate-900 transition hover:border-cyan-300 hover:text-cyan-700 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:hover:border-cyan-700 dark:hover:text-cyan-300"
                          >
                            Score {entry.boardScore}
                          </button>
                          <div>Best <span className="font-semibold text-slate-900 dark:text-slate-100">{entry.maxTile}</span></div>
                          <div>Moves <span className="font-semibold text-slate-900 dark:text-slate-100">{entry.moveCount}</span></div>
                        </div>
                      </div>

                      {expanded ? (
                        <div className="mt-3 w-full max-w-sm">
                          <Board grid={entry.grid} />
                        </div>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

Game2048.Guide = function Game2048Guide() {
  return (
    <div>
      <h3 className="mb-2 font-bold">How to Play 2048</h3>
      <p>Everyone gets the same starting 4x4 board and plays at the same time.</p>
      <p>Swipe or press a direction to slide every tile. Matching tiles merge into a larger value once per move.</p>
      <p>Your board score increases by the value of each merge, and the race ends as soon as someone reaches 2048.</p>
      <p>If no one reaches 2048, the round ends when every board is stuck. Highest tile wins, with score breaking ties.</p>
      <p>The progress panel shows each player&apos;s live board, score, best tile, and whether they are still alive.</p>
    </div>
  );
};

export default Game2048;